import React, { useEffect, useMemo, useState } from "react"
import dayjs from "dayjs"
import { Box, FormControlLabel, Grid, Paper, Typography } from "@mui/material"
import SideBarLayout from "src/v2/layout/side-bar-layout"
import DatePickerSelectV2 from "src/v2/components/Date/date-picker-select"
import DataGridTable from "src/v2/components/Table/data-grid-table"
import ButtonWithIcon from "src/v2/components/Button/Button"
import StyledCheckbox from "src/v2/components/checkbox/styled-checkbox"
import GenericModal from "src/v2/components/modal/generic-modal"
import GenericRadioButton from "src/v2/components/radio-button/generic-radio-button"
import MarketToMarketHighChart, { CHART_TYPE_LINE } from "src/v2/pages/DatalakeCompanies/tabs/market-to-market/market-to-market-high-chart"
import { ReactComponent as ChartIcon } from "src/v2/assets/icons/chart-icon.svg"
import { ReactComponent as FileIcon } from "src/v2/assets/icons/file-icon.svg"
import { dispatchSnackbar } from "src/v2/components/SnackBar/snackbar-bus"
import CurveService from "src/services/curve-service"
import { ExportFile } from "src/utils/export-file"
import { DATE_FORMAT, DATE_FORMAT_SHOW } from "src/utils/constants"

const TITLE = "Curvas ETTJC"
const INVALID_DATE_MESSAGE = "Informe uma data válida para consultar as Curvas ETTJC."
const DATE_CONFIRMATION_MESSAGE = "Pressione Enter para confirmar a data."

const GROUP_COLUMNS = [
    { field: "grupo", headerName: "Descrição", flex: 1, minWidth: 250, disableColumnMenu: true },
]

const CHART_OPTIONS = [
    { value: "taxa", label: "TAXA" },
    { value: "volatilidade", label: "VOLATILIDADE" },
]

const getClientId = () => {
    const jwt = localStorage.getItem("jwt")
    if (!jwt) return null

    try {
        const payloadPart = jwt.split(".")[1]
        const normalized = payloadPart.replace(/-/g, "+").replace(/_/g, "/")
        const payload = JSON.parse(atob(normalized))
        return payload && payload.user ? payload.user.IDCliente : null
    } catch (error) {
        return null
    }
}

const toChartSeries = (groups = []) => groups.flatMap((group) =>
    (group.items || []).map((item) => ({
        type: CHART_TYPE_LINE,
        label: `${group.grupo} - ${item.item}`,
        data: item.values,
        tooltip: { title: group.grupo, x: "DU", y: item.item },
    }))
)

const CurvasTirPage = () => {
    const curveService = useMemo(() => new CurveService(), [])
    const exportFile = useMemo(() => new ExportFile(), [])
    const clientId = useMemo(getClientId, [])
    const [loading, setLoading] = useState(false)
    const [dateSelected, setDateSelected] = useState("")
    const [lastAvailableDate, setLastAvailableDate] = useState("")
    const [dateError, setDateError] = useState("")
    const [datePendingConfirmation, setDatePendingConfirmation] = useState(false)
    const [status, setStatus] = useState("loading")
    const [retryVersion, setRetryVersion] = useState(0)
    const [groups, setGroups] = useState([])
    const [selectedGroupIds, setSelectedGroupIds] = useState([])
    const [ratings, setRatings] = useState([])
    const [selectedRatings, setSelectedRatings] = useState([])
    const [chartOpen, setChartOpen] = useState(false)
    const [chartView, setChartView] = useState("taxa")
    const [chartData, setChartData] = useState(null)
    const [csvData, setCsvData] = useState([])

    useEffect(() => {
        const jwt = localStorage.getItem("jwt")
        if (jwt) document.cookie = `c2p_jwt=${jwt}; domain=.c2ps.com.br; path=/`
    }, [])

    useEffect(() => {
        let ignoreResponse = false

        const loadFilters = async () => {
            setLoading(true)
            setStatus("loading")

            try {
                const [curvesResponse, ratingsResponse] = await Promise.all([
                    curveService.getCurvesTir(),
                    curveService.getRatingsByClient(clientId, "TIR"),
                ])
                if (ignoreResponse) return

                const referenceDate = curvesResponse && curvesResponse.body
                    ? curvesResponse.body.data_referencia
                    : null
                const parsedDate = dayjs(referenceDate)
                const ratingsBody = ratingsResponse && ratingsResponse.body ? ratingsResponse.body : {}
                const availableRatings = [...new Set(ratingsBody.ratings || [])]
                const descriptions = [...new Set(ratingsBody.descricoes || [])].sort()

                if (!referenceDate || !parsedDate.isValid()) {
                    setStatus("empty")
                    return
                }

                const formattedDate = parsedDate.format(DATE_FORMAT)
                setDateSelected(formattedDate)
                setLastAvailableDate(formattedDate)
                setGroups(descriptions.map((grupo, index) => ({ id: index, grupo })))
                setRatings(availableRatings)
                setSelectedRatings(availableRatings)
                setSelectedGroupIds([])
                setStatus("success")
            } catch (error) {
                if (ignoreResponse) return
                setStatus("error")
                dispatchSnackbar("Não foi possível carregar os filtros de Curvas ETTJC.", "error")
            } finally {
                if (!ignoreResponse) setLoading(false)
            }
        }

        loadFilters()
        return () => { ignoreResponse = true }
    }, [clientId, curveService, retryVersion])

    const getDateError = (value) => {
        const parsedDate = dayjs(value)
        if (!value || !parsedDate.isValid()) return INVALID_DATE_MESSAGE
        if (lastAvailableDate && parsedDate.isAfter(dayjs(lastAvailableDate), "day")) {
            return `A última data disponível é ${dayjs(lastAvailableDate).format(DATE_FORMAT_SHOW)}.`
        }
        return ""
    }

    const handleDateDraftChange = (value) => {
        const error = getDateError(value)
        const isCurrentDate = !error && dateSelected && dayjs(value).isSame(dayjs(dateSelected), "day")
        setDateError(error)
        setDatePendingConfirmation(!error && !isCurrentDate)
    }

    const changeDate = (value) => {
        const error = getDateError(value)
        if (error) {
            setDateError(error)
            setDatePendingConfirmation(false)
            return
        }
        setDateError("")
        setDatePendingConfirmation(false)
        setDateSelected(dayjs(value).format(DATE_FORMAT))
    }

    const toggleRating = (rating) => {
        setSelectedRatings((current) => current.includes(rating)
            ? current.filter((item) => item !== rating)
            : [...current, rating])
    }

    const generateChart = async () => {
        if (!selectedRatings.length) {
            dispatchSnackbar("Selecione ao menos um rating.", "warning")
            return
        }
        if (!selectedGroupIds.length) {
            dispatchSnackbar("Selecione ao menos um grupo.", "warning")
            return
        }
        if (!dateSelected || dateError || datePendingConfirmation) {
            dispatchSnackbar(INVALID_DATE_MESSAGE, "warning")
            return
        }

        setLoading(true)
        try {
            const selectedGroups = groups
                .filter((group) => selectedGroupIds.includes(group.id))
                .map((group) => group.grupo)
            const result = await curveService.generateCurveChart(selectedGroups, selectedRatings, dateSelected, "TIR")
            setChartData(result)
            setCsvData(result && result.taxa && result.taxa.rawData ? result.taxa.rawData : [])
            setChartView("taxa")
            setChartOpen(true)
        } catch (error) {
            dispatchSnackbar("Nenhum dado foi encontrado para os parâmetros informados.", "warning")
        } finally {
            setLoading(false)
        }
    }

    const exportCsv = () => {
        if (!csvData.length) return
        exportFile.toCSV(csvData, `${TITLE}_${dayjs(dateSelected).format(DATE_FORMAT_SHOW)}`, ";")
    }

    const currentChart = chartData
        ? (chartView === "taxa" ? chartData.taxa : chartData.volatilidade)
        : null
    const renderContent = () => {
        if (status === "error") return (
            <Box role="alert" sx={feedbackStyles}>
                <Typography variant="textBody" sx={{ color: "error.light" }}>Não foi possível carregar os filtros de Curvas ETTJC.</Typography>
                <ButtonWithIcon size="md" onClick={() => setRetryVersion((value) => value + 1)}>Tentar novamente</ButtonWithIcon>
            </Box>
        )
        if (status === "empty") return <Box sx={feedbackStyles}><Typography variant="textBody">Não há uma data disponível para Curvas ETTJC.</Typography></Box>
        if (status !== "success") return null

        return <DataGridTable
            columns={GROUP_COLUMNS}
            rows={groups}
            loading={loading}
            contained
            checkboxSelection
            disableRowSelectionOnClick
            onRowSelectionModelChange={(ids) => setSelectedGroupIds(Array.from(ids))}
            rowSelectionModel={selectedGroupIds}
            showPagination={false}
        />
    }

    return (
        <SideBarLayout title={TITLE} header variant="v1" loading={loading} disablePageScroll headerImage="/background/eventos_headers.jpg" headerPosition="1% 42%" headerTitle={TITLE}>
            <Box sx={{ mt: -2, height: "calc(100dvh - 202px)", maxHeight: "calc(100dvh - 202px)", minHeight: 0, pb: "8px", boxSizing: "border-box", display: "flex", flexDirection: "column", overflow: "hidden", gap: 2 }}>
                <Grid container sx={{ flexShrink: 0 }}>
                    <Grid item xs={12} xl={10}>
                        <Paper sx={{ p: 3 }}>
                            <Grid container justifyContent="space-between" alignItems="center" spacing={2}>
                                <Grid item xs={12} md={9}>
                                    <Grid container alignItems="center" spacing={3}>
                                        <Grid item xs={12} sm={4} md={3}>
                                            {status === "success" && <DatePickerSelectV2 value={dateSelected} label="Data de Exibição" onChange={changeDate} onDraftChange={handleDateDraftChange} commitOnAccept maxDate={lastAvailableDate} error={Boolean(dateError)} helperText={dateError || (datePendingConfirmation ? DATE_CONFIRMATION_MESSAGE : undefined)} />}
                                        </Grid>
                                        <Grid item xs={12} sm={8} md={9}>
                                            <Typography variant="contraHeading" sx={{ display: "block", mb: 0.5 }}>Ratings</Typography>
                                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                                {ratings.map((rating) => <FormControlLabel key={rating} label={rating} control={<StyledCheckbox checked={selectedRatings.includes(rating)} onChange={() => toggleRating(rating)} />} />)}
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item>
                                    <ButtonWithIcon size="md" icon={<ChartIcon />} onClick={generateChart} disabled={loading || status !== "success"}>Gerar Gráfico</ButtonWithIcon>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>
                </Grid>

                <Paper sx={{ flex: 1, p: 3, display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" }}>{renderContent()}</Paper>
            </Box>

            <GenericModal isOpen={chartOpen} onClose={() => setChartOpen(false)} title={`${TITLE} - ${dateSelected ? dayjs(dateSelected).format(DATE_FORMAT_SHOW) : ""}`} width="calc(100vw - 64px)" fullWidth>
                <Box sx={{ width: "100%", px: 3 }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 2 }}>
                        <GenericRadioButton title="" radioButtonItems={CHART_OPTIONS} defaultRadioValue="taxa" onChange={(event) => setChartView(event.target.value)} />
                        <ButtonWithIcon size="md" icon={<FileIcon />} onClick={exportCsv} disabled={!csvData.length}>Exportar CSV</ButtonWithIcon>
                    </Box>
                    {currentChart && <MarketToMarketHighChart xValue={currentChart.curva.values} data={toChartSeries(currentChart.grupos)} loading={loading} labelX={currentChart.curva.NomeCurva} labelY={chartView === "taxa" ? "Taxas %" : "Volatilidade %"} />}
                </Box>
            </GenericModal>
        </SideBarLayout>
    )
}

const feedbackStyles = { flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2, textAlign: "center" }

export default CurvasTirPage
